import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Cloud, Server, Shield, Zap, Database, Cpu } from 'lucide-react';
const providers = [{
  name: 'Amazon Web Services (AWS)',
  icon: Cloud,
  tag: 'Most flexible · Recommended',
  featured: true,
  description: 'Highly scalable with services like EC2 for hosting your app, S3 for storing media files, and CloudFront as a CDN. AWS also offers excellent security features and compliance tools out of the box.',
  points: ['EC2 compute & auto-scaling', 'S3 for media storage', 'CloudFront CDN', 'IAM, KMS & compliance tools']
}, {
  name: 'Google Cloud Platform (GCP)',
  icon: Database,
  tag: 'Most flexible · Recommended',
  featured: true,
  description: 'Similar to AWS in scale and capability — secure storage, strong compute, world-class analytics, and seamless integration with the rest of the Google ecosystem.',
  points: ['Cloud Storage & Compute Engine', 'Strong analytics (BigQuery)', 'Global CDN', 'ML/AI ready']
}, {
  name: 'Microsoft Azure',
  icon: Shield,
  tag: 'Enterprise-grade',
  description: 'Powerful, with enterprise-level security, deep compliance features, and global data centers for low-latency performance worldwide.',
  points: ['Enterprise compliance', 'Global data centers', 'Hybrid cloud support', 'Strong identity tooling']
}, {
  name: 'DigitalOcean',
  icon: Server,
  tag: 'Simple to start',
  description: 'Simpler and more straightforward — great for smaller-scale projects that need room to scale later. Managed databases, storage, and easy-to-deploy droplets.',
  points: ['Managed databases', 'Spaces object storage', 'Predictable pricing', 'Friendly developer UX']
}, {
  name: 'Vultr / Linode',
  icon: Cpu,
  tag: 'Cost-effective VPS',
  description: 'High-performance virtual private servers. Cost-effective and great if you want more control and don\u2019t mind managing infrastructure yourself.',
  points: ['Cheap, fast VPS', 'Global regions', 'Full root control', 'Good for lean teams']
}];
export function Launch() {
  return <div className="min-h-screen font-sans" style={{
    backgroundColor: '#1a0408',
    color: '#f5e6d3'
  }} data-id="element-141">
      {/* Cabaret ambient lighting */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" data-id="element-142">
        <motion.div className="absolute top-[-20%] left-[10%] w-[700px] h-[700px] rounded-full blur-3xl" style={{
        backgroundColor: 'rgba(220, 38, 38, 0.15)'
      }} animate={{
        scale: [1, 1.15, 1],
        x: [0, 40, 0]
      }} transition={{
        duration: 14,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-143" />
        <motion.div className="absolute bottom-[-20%] right-[5%] w-[600px] h-[600px] rounded-full blur-3xl" style={{
        backgroundColor: 'rgba(190, 24, 93, 0.18)'
      }} animate={{
        scale: [1, 1.2, 1],
        y: [0, -50, 0]
      }} transition={{
        duration: 16,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-144" />
      </div>

      <div className="relative z-10" data-id="element-145">
        {/* Top bar */}
        <header className="px-6 py-6 border-b" style={{
        borderColor: 'rgba(245, 230, 211, 0.1)'
      }} data-id="element-146">
          <div className="max-w-5xl mx-auto flex items-center justify-between" data-id="element-147">
            <Link to="/" className="inline-flex items-center gap-2 text-sm hover:opacity-80 transition-opacity" style={{
            color: '#f5e6d3'
          }} data-id="element-148">
              <ArrowLeft className="w-4 h-4" data-id="element-149" />
              Back to danceforfans.com
            </Link>
            <span className="text-xs uppercase tracking-[0.3em]" style={{
            color: '#d4a574'
          }} data-id="element-150">
              The Backstage
            </span>
          </div>
        </header>

        {/* Hero */}
        <section className="px-6 py-24 md:py-32" data-id="element-151">
          <div className="max-w-4xl mx-auto text-center" data-id="element-152">
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="text-sm uppercase tracking-[0.4em] mb-6" style={{
            color: '#d4a574'
          }} data-id="element-153">
              ✦ Velvet curtain · Backstage pass ✦
            </motion.p>

            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="font-serif italic text-5xl md:text-7xl lg:text-8xl leading-[1.05] mb-8" style={{
            color: '#f5e6d3'
          }} data-id="element-154">
              How to launch a platform like{' '}
              <span style={{
              color: '#dc2626'
            }} data-id="element-155">
                Dance for Fans
              </span>
              .
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="text-lg md:text-xl max-w-2xl mx-auto leading-relaxed" style={{
            color: 'rgba(245, 230, 211, 0.7)'
          }} data-id="element-156">
              Welcome backstage. Before the spotlight hits, every great show
              needs scaffolding — power, lighting, security, a stage that won't
              buckle when the crowd shows up. Here's what it takes.
            </motion.p>
          </div>
        </section>

        {/* Intro paragraph */}
        <section className="px-6 pb-16" data-id="element-157">
          <div className="max-w-3xl mx-auto" data-id="element-158">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.7
          }} className="border-l-2 pl-8 py-4" style={{
            borderColor: '#dc2626'
          }} data-id="element-159">
              <p className="font-serif text-2xl md:text-3xl italic leading-relaxed" style={{
              color: '#f5e6d3'
            }} data-id="element-160">
                For hosting, you'll need a provider that can handle large file
                uploads, secure payments, and high traffic.
              </p>
              <p className="mt-4 text-base" style={{
              color: 'rgba(245, 230, 211, 0.6)'
            }} data-id="element-161">
                Let's dive into the details — these are the top choices for an
                operation like this.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Providers */}
        <section className="px-6 pb-16" data-id="element-162">
          <div className="max-w-5xl mx-auto space-y-6" data-id="element-163">
            {providers.map((p, i) => {
            const Icon = p.icon;
            return <motion.article key={p.name} initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-50px'
            }} transition={{
              duration: 0.6,
              delay: i * 0.05
            }} className="rounded-2xl p-8 md:p-10 border backdrop-blur-sm" style={{
              backgroundColor: p.featured ? 'rgba(60, 8, 16, 0.7)' : 'rgba(40, 8, 14, 0.5)',
              borderColor: p.featured ? 'rgba(220, 38, 38, 0.4)' : 'rgba(245, 230, 211, 0.1)'
            }} data-id="element-164">
                  <div className="flex flex-col md:flex-row md:items-start gap-6" data-id="element-165">
                    <div className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0" style={{
                  backgroundColor: p.featured ? 'rgba(220, 38, 38, 0.2)' : 'rgba(245, 230, 211, 0.06)'
                }} data-id="element-166">
                      <Icon className="w-6 h-6" style={{
                    color: p.featured ? '#dc2626' : '#d4a574'
                  }} data-id="element-167" />
                    </div>

                    <div className="flex-1" data-id="element-168">
                      <div className="flex flex-wrap items-center gap-3 mb-3" data-id="element-169">
                        <h3 className="font-serif text-2xl md:text-3xl" style={{
                      color: '#f5e6d3'
                    }} data-id="element-170">
                          {p.name}
                        </h3>
                        <span className="text-xs uppercase tracking-widest px-3 py-1 rounded-full" style={{
                      backgroundColor: p.featured ? 'rgba(220, 38, 38, 0.15)' : 'rgba(212, 165, 116, 0.1)',
                      color: p.featured ? '#dc2626' : '#d4a574'
                    }} data-id="element-171">
                          {p.tag}
                        </span>
                      </div>

                      <p className="leading-relaxed mb-5" style={{
                    color: 'rgba(245, 230, 211, 0.75)'
                  }} data-id="element-172">
                        {p.description}
                      </p>

                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-173">
                        {p.points.map(pt => <li key={pt} className="flex items-center gap-2 text-sm" style={{
                      color: 'rgba(245, 230, 211, 0.65)'
                    }} data-id="element-174">
                            <span className="w-1.5 h-1.5 rounded-full" style={{
                        backgroundColor: p.featured ? '#dc2626' : '#d4a574'
                      }} data-id="element-175" />
                            {pt}
                          </li>)}
                      </ul>
                    </div>
                  </div>
                </motion.article>;
          })}
          </div>
        </section>

        {/* Recommendation */}
        <section className="px-6 py-20" data-id="element-176">
          <div className="max-w-3xl mx-auto" data-id="element-177">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.7
          }} className="rounded-3xl p-10 md:p-14 text-center border" style={{
            backgroundColor: 'rgba(60, 8, 16, 0.6)',
            borderColor: 'rgba(212, 165, 116, 0.3)'
          }} data-id="element-178">
              <Zap className="w-8 h-8 mx-auto mb-6" style={{
              color: '#d4a574'
            }} data-id="element-179" />
              <p className="text-xs uppercase tracking-[0.4em] mb-4" style={{
              color: '#d4a574'
            }} data-id="element-180">
                The bottom line
              </p>
              <p className="font-serif italic text-2xl md:text-3xl leading-snug mb-4" style={{
              color: '#f5e6d3'
            }} data-id="element-181">
                For a project like Dance for Fans — heavy on media, heavy on
                traffic —{' '}
                <span style={{
                color: '#dc2626'
              }} data-id="element-182">
                  AWS or GCP
                </span>{' '}
                are the most flexible, scalable choices.
              </p>
              <p className="text-base leading-relaxed" style={{
              color: 'rgba(245, 230, 211, 0.65)'
            }} data-id="element-183">
                They give you room to grow, with all the tools you need:
                storage, security, performance, and even machine learning when
                you're ready to add advanced features.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="px-6 py-10 border-t" style={{
        borderColor: 'rgba(245, 230, 211, 0.1)'
      }} data-id="element-184">
          <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm" data-id="element-185">
            <Link to="/" className="inline-flex items-center gap-2 hover:opacity-80 transition-opacity" style={{
            color: '#f5e6d3'
          }} data-id="element-186">
              <ArrowLeft className="w-4 h-4" data-id="element-187" />
              Back to the main stage
            </Link>
            <span style={{
            color: 'rgba(245, 230, 211, 0.5)'
          }} data-id="element-188">
              danceforfans.com · The Backstage
            </span>
          </div>
        </footer>
      </div>
    </div>;
}